/**
 * The arithmetic every detector shares: which body to read, where its parts are, how confident the
 * reading is, and — the one rule a child would notice immediately — which way round left and right
 * are (CAM-04).
 *
 * Pure functions over a {@link PoseFrame}. No state, no clock, no events.
 */

import type { BodyZone } from '../../shared/types/input-events';
import type { PoseCalibrationThresholds } from './pose-calibration';
import { POSE_LANDMARK, type PoseFrame, type PoseObservation } from './pose-types';

/** The body this single-player adapter is speaking for: the one the model is surest about. */
export function primaryPose(frame: PoseFrame): PoseObservation | null {
  let best: PoseObservation | null = null;
  for (const pose of frame.poses) {
    if (best === null || pose.coreConfidence > best.coreConfidence) best = pose;
  }
  return best;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Turns a sensor x into the x the child sees.
 *
 * The camera faces the child, so their own left is the raw frame's right; the feed is then mirrored
 * on screen so that moving left moves their image left, like a mirror (MVP notes §5). Anything that
 * compares a body against something drawn on screen has to make this flip, and it is made exactly
 * here so there is one place to be wrong.
 */
export function mirroredX(x: number): number {
  return clamp01(1 - clamp01(x));
}

/**
 * Confidence for one detection: the mean visibility of the landmarks this detector actually reads,
 * with the worst of them alongside (D-060(7)).
 *
 * Per-detection rather than per-frame because the alternative punishes the wrong thing: two hands
 * behind a child's back say nothing about whether they are crouching. A landmark that is not there
 * reads 0 — never 1 — so a truncated result cannot pass as a confident one.
 */
export function landmarkConfidence(
  pose: PoseObservation,
  indices: readonly number[],
): { mean: number; min: number } {
  if (indices.length === 0) return { mean: 0, min: 0 };

  let total = 0;
  let min = 1;
  for (const index of indices) {
    const visibility = clamp01(pose.landmarks[index]?.visibility ?? 0);
    total += visibility;
    if (visibility < min) min = visibility;
  }

  return { mean: total / indices.length, min };
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

function meanPoint(pose: PoseObservation, indices: readonly number[]): Point | null {
  let x = 0;
  let y = 0;
  for (const index of indices) {
    const point = pose.landmarks[index];
    if (point === undefined) return null;
    x += point.x;
    y += point.y;
  }
  return { x: x / indices.length, y: y / indices.length };
}

export function hipMidpoint(pose: PoseObservation): Point | null {
  return meanPoint(pose, [POSE_LANDMARK.leftHip, POSE_LANDMARK.rightHip]);
}

export function shoulderMidpoint(pose: PoseObservation): Point | null {
  return meanPoint(pose, [POSE_LANDMARK.leftShoulder, POSE_LANDMARK.rightShoulder]);
}

/** The point the lane detector tracks: shoulders and hips together, so an outflung arm is not a lane change. */
export function torsoCentre(pose: PoseObservation): Point | null {
  return meanPoint(pose, [
    POSE_LANDMARK.leftShoulder,
    POSE_LANDMARK.rightShoulder,
    POSE_LANDMARK.leftHip,
    POSE_LANDMARK.rightHip,
  ]);
}

/**
 * Which zone a body at this **raw** sensor x occupies, from the child's point of view.
 *
 * `current` is the zone the child is believed to be in; while it is set, the body must clear the
 * boundary by `laneHysteresis` before the zone changes, so a child standing on a line does not
 * flicker between two portals.
 */
export function bodyZoneFor(
  rawX: number,
  calibration: PoseCalibrationThresholds,
  current: BodyZone | null,
): BodyZone {
  const x = mirroredX(rawX);
  const { leftMax, rightMin } = calibration.laneBounds;
  const margin = current === null ? 0 : calibration.laneHysteresis;

  if (current === 'left') return x < leftMax + margin ? 'left' : plain(x, leftMax, rightMin);
  if (current === 'right') return x > rightMin - margin ? 'right' : plain(x, leftMax, rightMin);
  if (current === 'center') {
    if (x < leftMax - margin) return 'left';
    if (x > rightMin + margin) return 'right';
    return 'center';
  }
  return plain(x, leftMax, rightMin);
}

function plain(x: number, leftMax: number, rightMin: number): BodyZone {
  if (x < leftMax) return 'left';
  if (x > rightMin) return 'right';
  return 'center';
}
